import {
  Alert,
  AlertColor,
  Box,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type ToastVariant = "loading" | "success" | "error";

interface ToastState {
  open: boolean;
  message: string;
  subtitle?: string;
  variant: ToastVariant;
  duration: number | null;
}

interface ToastContextValue {
  showLoading: (message: string, subtitle?: string) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  dismissToast: () => void;
}

interface ToastProviderProps {
  children: ReactNode;
}

const DEFAULT_AUTO_HIDE_DURATION = 4000;

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const getToastBackgroundColor = (theme: Theme, variant: ToastVariant) => {
  if (variant === "success") {
    return theme.palette.success.main;
  }
  if (variant === "error") {
    return theme.palette.error.main;
  }

  return theme.palette.info.main;
};

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: "",
    subtitle: undefined,
    variant: "success",
    duration: DEFAULT_AUTO_HIDE_DURATION,
  });

  const showToast = useCallback(
    (
      variant: ToastVariant,
      message: string,
      duration: number | null,
      subtitle?: string,
    ) => {
      setToast({
        open: true,
        message,
        subtitle,
        variant,
        duration,
      });
    },
    [],
  );

  const showLoading = useCallback(
    (message: string, subtitle?: string) =>
      showToast("loading", message, null, subtitle),
    [showToast],
  );

  const showSuccess = useCallback(
    (message: string) =>
      showToast("success", message, DEFAULT_AUTO_HIDE_DURATION),
    [showToast],
  );

  const showError = useCallback(
    (message: string) => showToast("error", message, 6000),
    [showToast],
  );

  const dismissToast = useCallback(() => {
    setToast((current) => ({ ...current, open: false }));
  }, []);

  const handleClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") {
        return;
      }

      dismissToast();
    },
    [dismissToast],
  );

  const value = useMemo(
    () => ({
      dismissToast,
      showError,
      showLoading,
      showSuccess,
    }),
    [dismissToast, showError, showLoading, showSuccess],
  );

  const alertSeverity: AlertColor =
    toast.variant === "loading" ? "info" : toast.variant;

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={toast.duration}
        onClose={toast.variant === "loading" ? undefined : handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          variant="filled"
          severity={alertSeverity}
          icon={
            toast.variant === "loading" ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
          onClose={toast.variant === "loading" ? undefined : dismissToast}
          sx={(theme) => {
            const backgroundColor = getToastBackgroundColor(
              theme,
              toast.variant,
            );
            const textColor = theme.palette.getContrastText(backgroundColor);

            return {
              alignItems: "center",
              backgroundColor,
              boxShadow: 6,
              color: textColor,
              minWidth: 280,
              "& .MuiAlert-action": {
                color: textColor,
              },
              "& .MuiAlert-icon": {
                color: textColor,
              },
              "& .MuiAlert-message": {
                color: textColor,
                fontWeight: 600,
              },
              "& .MuiIconButton-root": {
                backgroundColor: "transparent",
                color: textColor,
                "&:hover": {
                  backgroundColor: "transparent",
                  opacity: 0.8,
                },
              },
            };
          }}
        >
          <Box>
            <Box component="span" sx={{ display: "block", fontWeight: 600 }}>
              {toast.message}
            </Box>
            {toast.subtitle ? (
              <Box
                component="span"
                sx={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: 1.35,
                  mt: 0.35,
                  opacity: 0.92,
                }}
              >
                {toast.subtitle}
              </Box>
            ) : null}
          </Box>
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  return context;
};
